// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { RootAssetsRepositoryState } from '@shared/data-access/interfaces/redux/assets-repository-state-types';
import { createTypedSelector } from '@shared/data-access/utils';
import {
  assetsInfoEntityAdapter,
  classificationsAdapter,
  ratesAdapter,
} from './assets-repository-state';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import type { CurrencyPairs } from '@shared/data-access/interfaces/api/brokerage/currency';

const assetsInfoSelectors = assetsInfoEntityAdapter.getSelectors();

const ratesSelectors = ratesAdapter.getSelectors();

const classificationsSelectors = classificationsAdapter.getSelectors();

const createSelector = createTypedSelector<RootAssetsRepositoryState>();

export const selectAssetsRepositoryState = (state: RootAssetsRepositoryState) =>
  state.assetsRepository;

export const selectAssetsInfo = createSelector(
  selectAssetsRepositoryState,
  ({ assets }) => assets,
);

export const selectAssetsInfoById = createSelector(
  [
    selectAssetsInfo,
    (_state, tickers: string[]) => tickers,
    (_state, _tickers: string[], currency: AccountCurrency) => currency,
  ],
  (state, tickers, currency) =>
    assetsInfoSelectors
      .selectAll(state)
      .filter(
        asset => tickers.includes(asset.ticker) && asset.currency === currency,
      ),
);

export const selectExchangeRates = createSelector(
  selectAssetsRepositoryState,
  ({ exchangeRates }) => exchangeRates,
);

export const selectExchangeRate = createSelector(
  [
    selectExchangeRates,
    (_state, baseCurrency: string, targetCurrency: string) =>
      baseCurrency + targetCurrency,
  ],
  (state, pair) => ratesSelectors.selectById(state, pair)?.rate,
);

export const selectRatesByCurrencyPairs = createSelector(
  [selectExchangeRates, (_state, pairs: CurrencyPairs) => pairs],
  (state, pairs) =>
    ratesSelectors.selectAll(state).filter(rate => pairs.includes(rate.pair)),
);

export const selectClassifications = createSelector(
  selectAssetsRepositoryState,
  ({ classifications }) => classifications,
);

export const selectClassificationById = createSelector(
  [selectClassifications, (_state, tickers: string[]) => tickers],
  (state, tickers) =>
    classificationsSelectors
      .selectAll(state)
      .filter(classification => tickers.includes(classification.ticker)),
);
